import Phaser from "phaser";
import { generateCreatures, chooseCreatures } from "../api";
import { FONT, UI, loadCreatureSprites, addCreatureSprite, type CreatureData } from "../types";
import { VIEWPORT, preloadUI, addBackground, addMusicBar, addEnterButton, addReturnButton } from "../ui";

export class ChooseCreaturesScene extends Phaser.Scene {
  private creatures: CreatureData[] = [];
  private sessionId = "";
  private selected = new Set<number>();
  private cardObjs: { bg: Phaser.GameObjects.Rectangle; check: Phaser.GameObjects.Text }[] = [];
  private statusText!: Phaser.GameObjects.Text;
  private confirmBtn: any;

  constructor() {
    super({ key: "ChooseCreaturesScene" });
  }

  preload(): void {
    preloadUI(this);
  }

  create(): void {
    this.selected.clear();
    this.cardObjs = [];

    addBackground(this);
    addMusicBar(this);

    this.add.text(180, 76, "Elegí 3 Guardianes", {
      fontFamily: FONT, fontSize: "16px", fontStyle: "bold", color: "#f5c242",
    }).setOrigin(0.5);

    this.statusText = this.add.text(VIEWPORT.x + VIEWPORT.w / 2, VIEWPORT.y + 14, "Cargando...", {
      fontFamily: FONT, fontSize: "10px", color: "#ccc",
    }).setOrigin(0.5);

    this.confirmBtn = addEnterButton(this, "CONFIRMAR 0/3", () => this.doChoose());
    this.confirmBtn.setEnabled(false);
    addReturnButton(this, "CANCELAR", () => {});

    this.loadCreatures();
  }

  private async loadCreatures(): Promise<void> {
    try {
      const res = await generateCreatures();
      this.sessionId = res.session_id;
      this.creatures = res.creatures.map((c: any) => ({
        template_name: c.name,
        sprite_key: c.sprite_key,
        sprite_url: c.sprite_url,
        level: 1,
        hp: c.hp,
        max_hp: c.max_hp,
        atk: c.atk,
        def_: c.def_,
        spd: c.spd,
        attacks: c.attacks,
      }));

      loadCreatureSprites(this, this.creatures, () => {
        this.statusText.setText("Tocá para seleccionar 3");
        this.drawCards();
      });
    } catch (err) {
      this.statusText.setText(err instanceof Error ? err.message : "Error");
    }
  }

  private drawCards(): void {
    // 5 cards dentro del viewport (316x240). Stack vertical, altura ~42 cada una.
    const startY = VIEWPORT.y + 30;
    const cardH = 40;
    const gap = 3;
    const w = VIEWPORT.w - 12;
    const baseX = VIEWPORT.x + 6;

    this.creatures.forEach((c, i) => {
      const y = startY + i * (cardH + gap) + cardH / 2;

      const bg = this.add.rectangle(baseX + w / 2, y, w, cardH, UI.PANEL, 1)
        .setInteractive({ useHandCursor: true });

      // Sprite
      addCreatureSprite(this, baseX + 22, y, c.sprite_key, 32);

      // Name
      this.add.text(baseX + 44, y - 12, c.template_name, {
        fontFamily: FONT, fontSize: "11px", fontStyle: "bold", color: "#fff",
      });

      // Stats
      const stats = `HP:${c.max_hp}  ATK:${c.atk}  DEF:${c.def_}  SPD:${c.spd}`;
      this.add.text(baseX + 44, y + 2, stats, {
        fontFamily: FONT, fontSize: "9px", color: "#ccc",
      });

      // Check mark
      const check = this.add.text(baseX + w - 14, y, "", {
        fontFamily: FONT, fontSize: "18px", color: "#f5c242",
      }).setOrigin(0.5);

      this.cardObjs.push({ bg, check });
      bg.on("pointerdown", () => this.toggleSelect(i));
    });
  }

  private toggleSelect(index: number): void {
    if (this.selected.has(index)) {
      this.selected.delete(index);
    } else if (this.selected.size < 3) {
      this.selected.add(index);
    }
    this.cardObjs.forEach((card, i) => {
      const isSelected = this.selected.has(i);
      card.bg.setStrokeStyle(isSelected ? 2 : 0, 0xf5c242);
      card.check.setText(isSelected ? "✓" : "");
    });
    const count = this.selected.size;
    this.confirmBtn.setLabel(`CONFIRMAR ${count}/3`);
    this.confirmBtn.setEnabled(count === 3);
  }

  private async doChoose(): Promise<void> {
    if (this.selected.size !== 3) return;
    this.confirmBtn.setLabel("Guardando...");
    this.confirmBtn.setEnabled(false);
    try {
      await chooseCreatures(this.sessionId, Array.from(this.selected));
      this.scene.start("MapScene");
    } catch (err) {
      this.statusText.setText(err instanceof Error ? err.message : "Error");
      this.confirmBtn.setLabel(`CONFIRMAR ${this.selected.size}/3`);
      this.confirmBtn.setEnabled(true);
    }
  }
}
